import React from "react";
import { Box, Typography } from "@mui/material";
import {
  CasinoOutlined,
  TrackChangesOutlined,
  BoltOutlined,
  StarOutline,
  DeviceHubOutlined,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import CommonButton from "../../ui/inputs/CommonButton";
import { useHistory } from "react-router-dom";
import { layoutSizes } from "../../../../theme/index.js";

const GAME_CONTENT = {
  "3-slot": {
    description: "Classic gameplay with big excitement.",
    features: [
      {
        icon: CasinoOutlined,
        title: "3 Reels",
        description: "Simple & fun",
      },
      {
        icon: TrackChangesOutlined,
        title: "1 Payline",
        description: "Match 3 symbols",
      },
      {
        icon: BoltOutlined,
        title: "Instant Play",
        description: "Quick rounds",
      },
    ],
  },

  "5-slot": {
    description: "More reels. More ways to win.",
    features: [
      {
        icon: CasinoOutlined,
        title: "5 Reels",
        description: "Bigger action",
      },
      {
        icon: DeviceHubOutlined,
        title: "Multiple Lines",
        description: "More winning chances",
      },
      {
        icon: StarOutline,
        title: "Higher Rewards",
        description: "Bigger jackpots",
      },
    ],
  },
};

const NewlyLaunchedCard = ({ game }) => {
  const theme = useTheme();
  const content = GAME_CONTENT[game.type];
  const history = useHistory();
  const handlePlayNow = () => {
    if (isThreeSlot) {
      history.push("/all-three-slotlottery");
    } else {
      history.push("/all-five-slotlottery");
    }
  };

  if (!content) {
    return null;
  }

  const isThreeSlot = game.type === "3-slot";

  const accentColor =
    game.accent === "red"
      ? theme.custom.colors.ribbon.deltaNegative
      : theme.custom.colors.blue[500];

  const accentLightColor =
    game.accent === "red"
      ? "rgba(255, 82, 101, 0.09)"
      : "rgba(52, 120, 246, 0.09)";

  return (
    <Box
      component="article"
      sx={{
        position: "relative",
        overflow: "hidden",
        border: "1px solid rgba(255, 255, 255, 0.9)",
        borderRadius: "18px",
        background: "#ffffff",
        boxShadow:
          "-4px -4px 10px rgba(28, 48, 85, 0.07), -1px -1px 3px rgba(28, 48, 85, 0.035)",
        isolation: "isolate",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Background */}
      <Box
        className="newly-launched-card__background"
        sx={{
          position: "absolute",
          inset: 0,
          zIndex: -1,
          backgroundImage: game.image
            ? `url(${game.image}), linear-gradient(120deg, rgba(255, 255, 255, 0.98) 0%, rgba(255, 255, 255, 0.92) 42%, rgba(255, 255, 255, 0.3) 100%)`
            : "linear-gradient(120deg, rgba(255, 255, 255, 0.98) 0%, rgba(255, 255, 255, 0.92) 42%, rgba(255, 255, 255, 0.3) 100%)",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      />

      {/* Content */}
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          padding: layoutSizes.homeCardPadding,
        }}
      >
        {/* Top badges */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box
            component="span"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              minHeight: "22px",
              padding: "0 8px",
              borderRadius: "999px",
              fontSize: "10px",
              fontWeight: 800,
              letterSpacing: "0.08em",
              color: accentColor,
              background: accentLightColor,
            }}
          >
            {game.category}
          </Box>

          <Box
            component="span"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              minHeight: "22px",
              padding: "0 8px",
              borderRadius: "999px",
              fontSize: "10px",
              fontWeight: 800,
              letterSpacing: "0.08em",
              color: accentColor,
              background: accentLightColor,
              gap: "5px",
            }}
          >
            <Box
              component="span"
              sx={{
                width: "5px",
                height: "5px",
                borderRadius: "50%",
                background: accentColor,
              }}
            />
            NEW
          </Box>
        </Box>

        {/* Game information */}
        <Box
          sx={{
            width: {
              xs: "65%",
              sm: "50%",
            },
            marginTop: "12px",
          }}
        >
          <Typography
            component="h3"
            sx={(theme) => ({
              ...(theme.typography.homeSection?.cardHeading || {}),
              margin: 0,
              display: "flex",
              alignItems: "baseline",
              letterSpacing: "-0.045em",
              color: "#0d1b36",
              "@media (max-width: 600px)": {
                fontSize: "20px",
              },
            })}
          >
            {isThreeSlot ? "3-" : "5-"}

            <Box
              component="span"
              sx={{
                color: accentColor,
                fontSize: "inherit",
                lineHeight: "inherit",
                fontWeight: "inherit",
                letterSpacing: "inherit",
              }}
            >
              Slot
            </Box>

            BET
          </Typography>

          <Typography
            sx={{
              margin: "6px 0 14px",
              fontSize: "12px",
              lineHeight: 1.4,
              color: "#6e7d96",
            }}
          >
            {content.description}
          </Typography>

          {/* Features */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
            }}
          >
            {content.features.map((feature) => {
              const Icon = feature.icon;

              return (
                <Box
                  key={feature.title}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "9px",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "27px",
                      height: "27px",
                      flex: "0 0 27px",
                      borderRadius: "50%",
                      background: "rgba(255, 255, 255, 0.82)",
                      boxShadow:
                        "2px 0 5px rgba(30, 50, 80, 0.06)",
                      color: accentColor,
                      "& svg": {
                        width: "14px",
                        height: "14px",
                      },
                    }}
                  >
                    <Icon />
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "1px",
                    }}
                  >
                    <Typography
                      component="span"
                      sx={{
                        fontSize: "10px",
                        fontWeight: 700,
                        color: "#172541",
                      }}
                    >
                      {feature.title}
                    </Typography>

                    <Typography
                      component="span"
                      sx={{
                        fontSize: "10px",
                        color: "#8290a6",
                      }}
                    >
                      {feature.description}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* Reusable CommonButton */}
        <Box sx={{ mt: "auto", pt: 2.5, width: "100%" }}>
          <CommonButton
            variant="playNow"
            size="small"
            fullWidth
            startIcon={null}
            onClick={handlePlayNow}
            sx={{
              minHeight: "38px",
              padding: "0px 16px",
              fontSize: "11px",
              borderRadius: "8px",
              background:
                game.accent === "red"
                  ? theme.custom.colors.ribbon.deltaNegative
                  : theme.custom.colors.blue[500],
              boxShadow:
                game.accent === "red"
                  ? "4px 0 10px rgba(240, 68, 85, 0.2)"
                  : "4px 0 10px rgba(52, 120, 246, 0.2)",
              "&:hover": {
                background:
                  game.accent === "red"
                    ? theme.custom.colors.ribbon.deltaNegative
                    : theme.custom.colors.blue[500],
                transform: "translateY(-2px)",
              },
            }}
          >
            Play Now
          </CommonButton>
        </Box>
      </Box>
    </Box>
  );
};

export default NewlyLaunchedCard;